import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { money, dateFr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, UploadSimple, Sparkle, PencilSimple, Trash, CheckCircle, ArrowCounterClockwise, Paperclip, EnvelopeSimple, CurrencyDollar, Receipt, BellRinging, ClockCountdown, Warning, ClockCounterClockwise } from "@phosphor-icons/react";

const empty = { date: "", due_date: "", vendor: "", description: "", category: "", subtotal: 0, tps_paid: 0, tvq_paid: 0, payment_status: "to_pay" };
const FILTERS = [{ v: "all", l: "Toutes" }, { v: "to_pay", l: "À payer" }, { v: "paid", l: "Payées" }];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [histFor, setHistFor] = useState(null);
  const [dispFor, setDispFor] = useState(null);
  const [dispResp, setDispResp] = useState("");
  const fileRef = useRef();

  const load = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/expenses${q}`).then((r) => setExpenses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/expenses/categories").then((r) => setCategories(r.data.categories)).catch(() => {}); }, []);

  const toPayTotal = expenses.filter((e) => e.payment_status !== "paid").reduce((s, e) => s + ((e.total || 0) - (e.amount_paid || 0)), 0);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e) => { setForm({ date: e.date, due_date: e.due_date || "", vendor: e.vendor, description: e.description, category: e.category, subtotal: e.subtotal, tps_paid: e.tps_paid, tvq_paid: e.tvq_paid, payment_status: e.payment_status }); setEditId(e.id); setOpen(true); };

  const suggestCategory = async () => {
    if (!form.vendor) { toast.error("Entrez d'abord le fournisseur."); return; }
    setAiLoading(true);
    try { const { data } = await api.post("/expenses/ai-categorize", { vendor: form.vendor, description: form.description }); setForm((f) => ({ ...f, category: data.category })); toast.success(`Catégorie suggérée : ${data.category}`); }
    catch (e) { toast.error(apiError(e)); } finally { setAiLoading(false); }
  };

  const save = async () => {
    if (!form.vendor) { toast.error("Le fournisseur est requis."); return; }
    const payload = { ...form, subtotal: Number(form.subtotal) || 0, tps_paid: Number(form.tps_paid) || 0, tvq_paid: Number(form.tvq_paid) || 0, category: form.category || null, date: form.date || null };
    try {
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editId ? "Dépense mise à jour" : "Dépense ajoutée"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const upload = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setUploading(true);
    toast.loading("Lecture de la facture par l'IA…", { id: "up" });
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/expenses/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Dépense créée : ${data.vendor} — ${data.category}`, { id: "up" }); load();
    } catch (e) { toast.error(apiError(e), { id: "up" }); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const checkInbox = async () => {
    toast.loading("Vérification du courriel…", { id: "inbox" });
    try { const { data } = await api.post("/email/check-inbox"); toast.success(`${data.processed} facture(s) importée(s) du courriel`, { id: "inbox" }); load(); }
    catch (e) { toast.error(apiError(e), { id: "inbox" }); }
  };

  const sendReminders = async () => {
    toast.loading("Envoi des rappels…", { id: "rem" });
    try { const { data } = await api.post("/email/send-reminders"); toast.success(data.reminded ? `Rappel envoyé pour ${data.reminded} dépense(s)` : (data.reason || "Aucune dépense à rappeler"), { id: "rem" }); }
    catch (e) { toast.error(apiError(e), { id: "rem" }); }
  };

  const dueInfo = (e) => {
    if (!e.due_date || e.payment_status === "paid") return null;
    const today = new Date().toISOString().slice(0, 10);
    const diff = Math.ceil((new Date(e.due_date + "T00:00:00") - new Date(today + "T00:00:00")) / 86400000);
    if (diff < 0) return { label: `En retard (${e.due_date})`, cls: "text-red-600", icon: Warning };
    if (diff <= 3) return { label: `Échéance ${e.due_date}`, cls: "text-amber-600", icon: ClockCountdown };
    return { label: `Échéance ${e.due_date}`, cls: "text-muted-foreground", icon: ClockCountdown };
  };

  const togglePaid = async (e) => {
    try {
      const { data } = await api.post(`/expenses/${e.id}/toggle-paid`);
      if (data.payment_status === "paid") {
        if (data.email_sent) toast.success(`Payée — reçu ${data.confirmation_number} envoyé au fournisseur (Innova-Pay)`);
        else toast.success(`Payée — confirmation ${data.confirmation_number} générée (courriel non envoyé : ${data.supplier_email ? "SMTP non configuré" : "aucun courriel fournisseur"})`);
      } else {
        toast.success("Remise à « à payer »");
      }
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openPay = (e) => { setPayFor(e); setPayAmount(String(Math.max((e.total || 0) - (e.amount_paid || 0), 0).toFixed(2))); };
  const submitPay = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) { toast.error("Montant invalide."); return; }
    try {
      const { data } = await api.post(`/expenses/${payFor.id}/add-payment`, { amount: amt });
      const label = data.payment_status === "paid" ? "Facture payée en totalité" : `Paiement partiel enregistré (reste ${money(data.remaining)})`;
      if (data.email_sent) toast.success(`${label} — reçu ${data.confirmation_number} envoyé (Innova-Pay)`);
      else toast.success(`${label} — confirmation ${data.confirmation_number}`);
      setPayFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Dépense supprimée"); load(); } catch (e) { toast.error(apiError(e)); } };

  const resolveDispute = async () => {
    try {
      await api.post(`/expenses/${dispFor.id}/resolve-dispute`, { response: dispResp });
      toast.success("Écart marqué comme résolu");
      setDispFor(null); setDispResp(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Dépenses</h1>
          <p className="text-muted-foreground mt-1">Saisie manuelle, lecture IA et traitement des paiements à faire.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid="expense-file-input" />
          <Button data-testid="send-reminders-btn" variant="outline" className="rounded-sm" onClick={sendReminders}><BellRinging size={18} className="mr-1" /> Rappels d'échéance</Button>
          <Button data-testid="check-inbox-btn" variant="outline" className="rounded-sm" onClick={checkInbox}><EnvelopeSimple size={18} className="mr-1" /> Vérifier le courriel</Button>
          <Button data-testid="upload-expense-btn" variant="outline" className="rounded-sm" onClick={() => fileRef.current?.click()} disabled={uploading}><UploadSimple size={18} className="mr-1" /> {uploading ? "Lecture…" : "Importer un reçu (IA)"}</Button>
          <Button data-testid="new-expense-btn" className="rounded-sm" onClick={openNew}><Plus size={18} className="mr-1" /> Nouvelle dépense</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">À payer</div>
          <div className="mt-2 font-heading text-2xl font-bold tabular-nums text-red-600" data-testid="to-pay-total">{money(toPayTotal)}</div>
        </div>
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">TPS payée (CTI)</div>
          <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{money(expenses.reduce((s, e) => s + e.tps_paid, 0))}</div>
        </div>
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">TVQ payée (RTI)</div>
          <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{money(expenses.reduce((s, e) => s + e.tvq_paid, 0))}</div>
        </div>
      </div>

      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button key={f.v} data-testid={`exp-filter-${f.v}`} onClick={() => setFilter(f.v)}
            className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${filter === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"}`}>{f.l}</button>
        ))}
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Date</TableHead><TableHead>Fournisseur</TableHead><TableHead>Catégorie</TableHead>
              <TableHead className="text-right">TPS</TableHead><TableHead className="text-right">TVQ</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Source</TableHead>
              <TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Aucune dépense.</TableCell></TableRow>}
            {expenses.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-sm text-muted-foreground">{dateFr(e.date)}</TableCell>
                <TableCell className="font-medium">{e.vendor}</TableCell>
                <TableCell><span className="text-xs px-2 py-0.5 rounded-sm border border-border bg-muted">{e.category}</span></TableCell>
                <TableCell className="text-right tabular-nums text-sm">{money(e.tps_paid)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{money(e.tvq_paid)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">{money(e.total)}</TableCell>
                <TableCell><span className="text-xs text-muted-foreground">{e.source === "email" ? "Courriel" : e.source === "upload" ? "IA" : "Manuel"}</span></TableCell>
                <TableCell>
                  {e.payment_status === "paid" ? (
                    <span className="text-xs px-2 py-0.5 rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-200">Payée</span>
                  ) : e.payment_status === "partial" ? (
                    <span className="text-xs px-2 py-0.5 rounded-sm border bg-blue-50 text-blue-800 border-blue-200" title={`Payé ${money(e.amount_paid)} / ${money(e.total)}`}>Partiel · reste {money((e.total || 0) - (e.amount_paid || 0))}</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-sm border bg-amber-50 text-amber-800 border-amber-200">À payer</span>
                  )}
                  {(() => { const d = dueInfo(e); return d ? <div className={`mt-1 flex items-center gap-1 text-xs ${d.cls}`}><d.icon size={12} />{d.label}</div> : null; })()}
                  {e.dispute && (
                    <button data-testid={`view-dispute-${e.id}`} onClick={() => { setDispFor(e); setDispResp(e.dispute.response || ""); }}
                      className={`mt-1 flex items-center gap-1 text-xs ${e.dispute.status === "resolved" ? "text-emerald-600" : "text-red-600 font-medium"}`}>
                      <Warning size={12} /> {e.dispute.status === "resolved" ? "Écart résolu" : "Écart signalé"}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {e.attachment_path && <Button variant="ghost" size="icon" className="h-8 w-8" title="Pièce jointe" onClick={() => window.open(`${API}/expenses/${e.id}/attachment?auth=${localStorage.getItem("token")}`, "_blank")}><Paperclip size={16} /></Button>}
                    {(e.payments && e.payments.length > 0) && <Button data-testid={`history-${e.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Historique des paiements" onClick={() => setHistFor(e)}><ClockCounterClockwise size={16} /></Button>}
                    {(e.payments && e.payments.length > 0) && <Button data-testid={`receipt-${e.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Dernier reçu" onClick={() => window.open(`${API}/expenses/${e.id}/receipt?auth=${localStorage.getItem("token")}`, "_blank")}><Receipt size={16} /></Button>}
                    {e.payment_status !== "paid" && <Button data-testid={`pay-${e.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Enregistrer un paiement" onClick={() => openPay(e)}><CurrencyDollar size={16} /></Button>}
                    <Button data-testid={`toggle-paid-${e.id}`} variant="ghost" size="icon" className="h-8 w-8" title={e.payment_status === "paid" ? "Annuler le paiement" : "Payer en totalité"} onClick={() => togglePaid(e)}>{e.payment_status === "paid" ? <ArrowCounterClockwise size={16} /> : <CheckCircle size={16} />}</Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(e)}><PencilSimple size={16} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => del(e.id)}><Trash size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier la dépense" : "Nouvelle dépense"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <F label="Date"><Input type="date" className="rounded-sm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></F>
              <F label="Fournisseur *"><Input data-testid="expense-vendor" className="rounded-sm" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></F>
            </div>
            <F label="Date d'échéance (pour les rappels de paiement)"><Input data-testid="expense-due-date" type="date" className="rounded-sm" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></F>
            <F label="Description"><Textarea className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
            <F label="Catégorie">
              <div className="flex gap-2">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category" className="rounded-sm"><SelectValue placeholder="Choisir ou suggérer par IA" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" data-testid="ai-suggest-btn" variant="outline" className="rounded-sm shrink-0" onClick={suggestCategory} disabled={aiLoading}><Sparkle size={16} className="mr-1" /> {aiLoading ? "…" : "IA"}</Button>
              </div>
            </F>
            <div className="grid grid-cols-3 gap-4">
              <F label="Sous-total"><Input type="number" step="0.01" className="rounded-sm tabular-nums" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} /></F>
              <F label="TPS payée"><Input type="number" step="0.01" className="rounded-sm tabular-nums" value={form.tps_paid} onChange={(e) => setForm({ ...form, tps_paid: e.target.value })} /></F>
              <F label="TVQ payée"><Input type="number" step="0.01" className="rounded-sm tabular-nums" value={form.tvq_paid} onChange={(e) => setForm({ ...form, tvq_paid: e.target.value })} /></F>
            </div>
            <div className="text-right text-sm text-muted-foreground">Total : <span className="font-medium text-foreground tabular-nums">{money((Number(form.subtotal) || 0) + (Number(form.tps_paid) || 0) + (Number(form.tvq_paid) || 0))}</span></div>
            <F label="Statut de paiement">
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="to_pay">À payer</SelectItem><SelectItem value="paid">Payée</SelectItem></SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-expense-btn" className="rounded-sm" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CurrencyDollar size={20} /> Enregistrer un paiement</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                <div><span className="text-foreground font-medium">{payFor.vendor}</span></div>
                <div className="flex justify-between mt-2"><span>Total facture</span><span className="tabular-nums text-foreground">{money(payFor.total)}</span></div>
                <div className="flex justify-between"><span>Déjà payé</span><span className="tabular-nums text-foreground">{money(payFor.amount_paid || 0)}</span></div>
                <div className="flex justify-between font-medium"><span>Solde restant</span><span className="tabular-nums text-red-600">{money((payFor.total || 0) - (payFor.amount_paid || 0))}</span></div>
              </div>
              <F label="Montant de ce paiement">
                <Input data-testid="payment-amount" type="number" step="0.01" className="rounded-sm tabular-nums" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
              </F>
              <p className="text-xs text-muted-foreground">Un numéro de confirmation Innova-Pay sera généré et, si le fournisseur a un courriel (SMTP configuré), un reçu PDF lui sera envoyé.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPayFor(null)}>Annuler</Button>
            <Button data-testid="submit-payment-btn" className="rounded-sm" onClick={submitPay}>Enregistrer le paiement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment history dialog */}
      <Dialog open={!!histFor} onOpenChange={(v) => !v && setHistFor(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ClockCounterClockwise size={20} /> Historique des paiements</DialogTitle></DialogHeader>
          {histFor && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{histFor.vendor} · Total {money(histFor.total)} · Payé {money(histFor.amount_paid || 0)}</div>
              <div className="border border-border rounded-sm divide-y divide-border">
                {(histFor.payments || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`payment-row-${i}`}>
                    <div>
                      <div className="font-medium tabular-nums">{money(p.amount)}</div>
                      <div className="text-xs text-muted-foreground">{p.date} · <span className="font-mono">{p.confirmation_number}</span></div>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-sm" onClick={() => window.open(`${API}/expenses/${histFor.id}/receipt?conf=${p.confirmation_number}&auth=${localStorage.getItem("token")}`, "_blank")}>
                      <Receipt size={14} className="mr-1" /> Reçu
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Dispute (écart) dialog */}
      <Dialog open={!!dispFor} onOpenChange={(v) => !v && setDispFor(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Warning size={20} /> Écart signalé par le fournisseur</DialogTitle></DialogHeader>
          {dispFor?.dispute && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">{dispFor.vendor} · <span className="tabular-nums text-foreground">{money(dispFor.total)}</span> · {dateFr(dispFor.date)}</div>
              <div className="border border-border rounded-sm p-3 bg-muted/40 text-sm">
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Message du fournisseur</div>
                <div className="whitespace-pre-wrap">{dispFor.dispute.message}</div>
                <div className="text-xs text-muted-foreground mt-2">Signalé par {dispFor.dispute.by || "—"} · {dateFr(dispFor.dispute.created_at)}</div>
              </div>
              {dispFor.dispute.status === "resolved" ? (
                <div className="text-sm text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-sm px-3 py-2">
                  Écart résolu{dispFor.dispute.response ? ` — ${dispFor.dispute.response}` : ""}
                </div>
              ) : (
                <F label="Réponse / note de résolution (optionnel)">
                  <Textarea data-testid="dispute-response" className="rounded-sm" value={dispResp} onChange={(e) => setDispResp(e.target.value)} placeholder="Ex. : montant corrigé, crédit émis, etc." />
                </F>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDispFor(null)}>Fermer</Button>
            {dispFor?.dispute?.status !== "resolved" && <Button data-testid="resolve-dispute-btn" className="rounded-sm" onClick={resolveDispute}>Marquer comme résolu</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>{children}</div>;
}
